// Strips personal data from the URL before Sentry and analytics read it.
import "./utils/privateParams";
// Sentry
import "./sentry/instrument.ts";
import * as Sentry from "@sentry/react";
// Hotjar
import { hotjar } from "react-hotjar";
// Routes
import { createRoot } from "react-dom/client";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
// Components
import App from "./App.tsx";
import PreconfirmationPage from "./components/PreconfirmationPage/PreconfirmationPage.tsx";
import TemplateInterpreter from "./components/TemplateInterpreter";
import NotFoundPage from "./components/NotFoundPage/NotFoundPage.tsx";
import MaintenanceScreen from "./components/MaintenanceScreen/MaintenanceScreen.tsx";
import RemoteStep from "./components/RemoteStep/RemoteStep.tsx";
import {
  isAllariaDigitalOnboarding,
  isAllariaFondosOnboarding,
  isAllariaOnboarding,
} from "./utils";
import { startEarlyTokenHandshake } from "./utils/crypto.ts";

// AWS Liveness
import "@aws-amplify/ui-react/styles.css";
import { ThemeProvider } from "@aws-amplify/ui-react";

// Redux
import { Provider } from "react-redux";
import { store } from "./app/store.ts";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
// Firebase
import { initializeApp } from "firebase/app";
import { getStorage } from "firebase/storage";
import { getFirestore } from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics";
import SuccessPage from "./components/SuccessPage/SuccessPage.tsx";
import ErrorPage from "./components/ErrorPage/ErrorPage.tsx";
import PartnerConsentPage from "./components/PartnerConsentPage/PartnerConsentPage.tsx";
import FalFundSelectionPage from "./components/FalFundSelectionPage/FalFundSelectionPage.tsx";
import { partnerConsentFlows } from "./utils/partnerConsent.ts";
import { getCompanyData } from "./utils/companies.tsx";
import EmailVerificationPage from "./components/EmailVerificationPage/EmailVerificationPage.tsx";
import { Amplify } from "aws-amplify";
import awsexports from "./aws-exports.ts";

// Styles
import "@allaria-tech/matter/index.css";
import "./styles/global.scss";

const container = document.getElementById("root")!;
const root = createRoot(container);
export const persistor = persistStore(store);

if (import.meta.env.VITE_ENVIRONMENT !== "dev") {
  hotjar.initialize({
    id: Number(import.meta.env.VITE_HOTJAR_ID),
    sv: Number(import.meta.env.VITE_HOTJAR_SV),
  });
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_API_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_API_PROJECT_ID,
  storageBucket: import.meta.env.VITE_API_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_API_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_API_APP_ID,
  measurementId: import.meta.env.VITE_API_MEASUREMENT_ID,
};

//Initialize Firebase
export const app = initializeApp(firebaseConfig);
getStorage(app);
getFirestore(app);
const analytics = getAnalytics(app);

export const logAnalyticsAndHotjarEvent = (
  eventName: string,
  details?: Record<string, unknown>,
) => {
  if (import.meta.env.VITE_ENVIRONMENT !== "dev") {
    //Google Analytics
    logEvent(analytics, eventName, details);
    //Hotjar
    if (hotjar.initialized()) hotjar.event(eventName);
  }
};

// Liveness Config
Amplify.configure(awsexports);

// Allaria Digital only has the crypto onboarding, which lives on /digital.
const isDigitalOnboarding = isAllariaDigitalOnboarding();

// Announced here rather than from useCryptoBridge: the Allaria app only waits
// TOKEN_HANDSHAKE_TIMEOUT_MS for it, and on a cold load mounting the React tree
// takes longer than that, so the app gave up on an onboarding that was fine.
if (isDigitalOnboarding) startEarlyTokenHandshake();

const defaultOnboardingPath = isDigitalOnboarding
  ? "/digital"
  : isAllariaFondosOnboarding()
    ? "/empresarial"
    : "/personal";

// The backend only accepts coowners for ALLARIA, so the routes aren't
// available for the rest of the companies.
const coownersRoutes = isAllariaOnboarding()
  ? [
      {
        path: "/cotitularidad",
        element: <TemplateInterpreter />,
      },
      {
        path: "/cotitular-adicional",
        element: <TemplateInterpreter />,
      },
    ]
  : [];

// The generic onboardings don't exist for Allaria Digital: that build is only
// the crypto onboarding embedded in the Allaria app. Registering them anyway
// left a complete, unguarded flow reachable on the crypto domain — useCryptoGuard
// only covers onboardingType === "DIGITAL", so /personal & co. never ran any
// embed check, and the CSP frame-ancestors header only restricts who may frame
// the page, not opening it top level.
const genericOnboardingRoutes = isDigitalOnboarding
  ? []
  : [
      {
        path: "/empresarial",
        element: <TemplateInterpreter />,
      },
      {
        path: "/firmante",
        element: <TemplateInterpreter />,
      },
      {
        path: "/personal",
        element: <TemplateInterpreter />,
      },
      ...coownersRoutes,
      {
        path: "/securities",
        element: <TemplateInterpreter />,
      },
    ];

// Consent flows for people arriving from a partner's app, one pair of routes
// per partner in the registry — adding a bank adds its routes. On token links
// the token is the last segment of the path and is the credential the backend
// validates; that shape is fixed by the link generator, which appends `/{token}` to the
// LANDING_BASE_URL it is configured with, so whatever we hand a partner has to
// end at their route.
//
// Each flow names the company that serves it, and only that build mounts it.
// Not keyed off `partnerships`: `getPartnership` matches keys as substrings of
// the query string, so an "allaria" entry under ALLARIAFONDOS would tag every
// Fondos onboarding whose URL mentions Allaria.
const { companyName } = getCompanyData();

const partnerConsentRoutes = partnerConsentFlows
  .filter((flow) => flow.host.company === companyName)
  .flatMap((flow) => [
    ...(flow.link === "token"
      ? [
          {
            path: `/${flow.route}/:token`,
            element: <PartnerConsentPage flow={flow} />,
          },
        ]
      : []),
    // A token flow lands on the invalid-link screen here, which is also what
    // makes `?mock=…` usable for walking its screens in dev. A params flow
    // lives only here.
    {
      path: `/${flow.route}`,
      element: <PartnerConsentPage flow={flow} />,
    },
  ]);

// The mirror of the above: the crypto onboarding is only served by the Allaria
// Digital build, the only one deployed with the allowed parent origins and the
// frame-ancestors header the iframe handshake depends on.
const cryptoOnboardingRoutes = isDigitalOnboarding
  ? [
      {
        path: "/digital",
        element: <TemplateInterpreter />,
      },
    ]
  : [];

// Linked from the FAL welcome email (operation-OB-A).
const falFundSelectionRoutes =
  companyName === "ALLARIAFONDOS"
    ? [{ path: "/elegi-tu-fal", element: <FalFundSelectionPage /> }]
    : [];

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        path: "*",
        element: <NotFoundPage />,
      },
      {
        path: "/",
        element: <Navigate to={defaultOnboardingPath} replace />,
      },
      ...genericOnboardingRoutes,
      ...partnerConsentRoutes,
      ...falFundSelectionRoutes,
      {
        path: "/dni",
        element: <RemoteStep />,
      },
      {
        path: "/identidad",
        element: <RemoteStep />,
      },
      {
        path: "/preconfirmacion",
        element: <PreconfirmationPage />,
      },
      {
        path: "/registro-enviado",
        element: <SuccessPage />,
      },
      {
        path: "/link-expirado",
        element: <ErrorPage />,
      },
      {
        path: "/verificacion-email",
        element: <EmailVerificationPage />,
      },
      ...cryptoOnboardingRoutes,
    ],
  },
]);

root.render(
  <Provider store={store}>
    <MaintenanceScreen>
      <ThemeProvider>
        <PersistGate loading={null} persistor={persistor}>
          <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
            <RouterProvider router={router} />
          </Sentry.ErrorBoundary>
        </PersistGate>
      </ThemeProvider>
    </MaintenanceScreen>
  </Provider>,
);
